import { useCallback, useEffect, useState } from 'react';
import { useBetslipReofferedIds } from '../stores/betslip/selectors';
import { getLocalizedBettingTypeFromHitCount } from '../helpers';
import { useBetslipById } from '../queries';
import { getReofferedColor } from '../utils';
import useLocalization from './useLocalization';
import { useMarketplaceConfig } from '../queries/marketplace';

export const useReofferedBetslip = () => {
  const [open, setOpen] = useState(false);
  const [currentBetslip, setCurrentBetslip] = useState<string>('');

  const reofferedBettingSlipsIds = useBetslipReofferedIds();
  const { t } = useLocalization();

  useEffect(() => {
    setOpen(reofferedBettingSlipsIds.length > 0);
    if (!currentBetslip || !reofferedBettingSlipsIds.includes(currentBetslip)) {
      setCurrentBetslip(reofferedBettingSlipsIds[0] ?? '');
    }
  }, [currentBetslip, reofferedBettingSlipsIds]);

  const { showReofferComparison } = useMarketplaceConfig();
  const { data: betslip } = useBetslipById(currentBetslip);
  const { data: reofferedFromBetslip } = useBetslipById(betslip?.reofferedFromId ?? undefined, {
    enabled: showReofferComparison && !!betslip?.reofferedFromId,
  });

  const bets = betslip?.bets ?? [];
  const previousBets = reofferedFromBetslip?.bets ?? [];
  const betBuilderGroups = betslip?.betBuilder ?? [];
  const betsCount = bets.length + betBuilderGroups.length;

  const isBanker = bets.some((bet) => bet.banker);

  const { color: totalOddsColor, changed: totalOddsChanged } = getReofferedColor(
    betslip?.totalOdds,
    reofferedFromBetslip?.totalOdds
  );
  const { color: stakeAmountColor, changed: stakeAmountChanged } = getReofferedColor(
    betslip?.stakeAmountPerCombination,
    reofferedFromBetslip?.stakeAmountPerCombination
  );
  const { color: maxPayoutColor, changed: maxPayoutChanged } = getReofferedColor(
    betslip?.maxPayout,
    reofferedFromBetslip?.maxPayout
  );

  const localizedBettingType = getLocalizedBettingTypeFromHitCount(t, betslip?.requiredHitCount || 0);

  const showBonusData = (betslip?.bonus?.bonusAmount ?? 0) > 0;
  const betslipHeaderTitle = showReofferComparison ? t('betslipReoffered') : t('betslip');

  const currentIndex = reofferedBettingSlipsIds.indexOf(currentBetslip);
  const totalSlips = reofferedBettingSlipsIds.length;
  const isPrevDisabled = currentIndex === 0 || totalSlips === 1;
  const isNextDisabled = currentIndex === totalSlips - 1 || totalSlips === 1;

  const goToPrev = useCallback(() => {
    if (!isPrevDisabled) setCurrentBetslip(reofferedBettingSlipsIds[currentIndex - 1]);
  }, [currentIndex, isPrevDisabled, reofferedBettingSlipsIds]);

  const goToNext = useCallback(() => {
    if (!isNextDisabled) setCurrentBetslip(reofferedBettingSlipsIds[currentIndex + 1]);
  }, [currentIndex, isNextDisabled, reofferedBettingSlipsIds]);

  return {
    open,
    currentBetslip,
    betslip,
    reofferedFromBetslip,
    bets,
    previousBets,
    betBuilderGroups,
    betsCount,
    isBanker,
    totalOddsColor,
    totalOddsChanged,
    stakeAmountColor,
    stakeAmountChanged,
    maxPayoutColor,
    maxPayoutChanged,
    localizedBettingType,
    showBonusData,
    betslipHeaderTitle,
    showReofferComparison,
    reofferedBettingSlipsIds,
    currentIndex,
    totalSlips,
    isPrevDisabled,
    isNextDisabled,
    goToPrev,
    goToNext,
    t,
  };
};
